import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  withStyles,
} from "@material-ui/core";
import { useState, useEffect } from "react";
import Tablestyles from "../ROCITable.module.css";
import { Autocomplete, TextField, Checkbox } from "@mui/material";
import Chip from "@mui/material/Chip";

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: "#e5e5e5",
    color: "#212529",
    height: 3,
    width: "auto", // let content decide
    minWidth: "30px",
    maxWidth: "250px",
    whiteSpace: "normal",
  },
  body: {
    fontSize: 12,
    fontFamily: "Roboto,Helvetica,Arial,sans-serif",
    width: "auto", // let content decide
    minWidth: "30px",
    maxWidth: "250px",
    whiteSpace: "normal",
  },
  root: {
    padding: "15px",
  },
}))(TableCell);

const Row = (props) => {
  const {
    TableColumns,
    RowData,
    allModelData,
    AllModelsMetaData,
    sourceData,
    updateSourceInputs,
  } = props;
  // DATA STATES
  const [bestScoreModel, setBestScoreModel] = useState({});
  const [allModelsMetaData, setAllModelsMetaData] = useState([]);
  const [rowSpan, setRowSpan] = useState(2);
  const SKU_DESCRIPTION_NAME =
    sourceData?.data?.dropdown?.recommendation_details
      ?.sku_description_column_name || "";
  const DROPDOWN_OPTIONS = [
    { label: "Select All", name: "select_all", value: "select_all" },
    ...(sourceData?.data?.dropdown?.recommendation_details?.recommendation_modal_column_list?.initial_header_column_list?.filter(
      (item) => item?.name !== SKU_DESCRIPTION_NAME
    ) || []),
  ];

  // ACTION STATES
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [overideModel, setOverrideModel] = useState(null);
  const [selectedModel, setSelectedModel] = useState();
  const [expanded, setExpanded] = useState(false);

  const isValidMatch = (a, b) =>
    a != null && b != null && a !== "" && b !== "" && a === b;

  const sortBestScoreModelAndAllModelMetaData = () => {
    // ALL MODEL DATAS FOR CURRENT SKU
    const All_Model_Data_For_Current_Record = Object.keys(allModelData || {})
      .map((item) => {
        return allModelData[item]?.find((rec) => {
          const recValue = rec?.[SKU_DESCRIPTION_NAME];
          const rowValue = RowData?.rowData?.[SKU_DESCRIPTION_NAME];
          return isValidMatch(recValue, rowValue);
        });
      })
      .filter((record) => record !== undefined);
    setAllModelsMetaData(All_Model_Data_For_Current_Record);

    // BEST MODEL DATA
    const bestModel =
      All_Model_Data_For_Current_Record.length > 0
        ? All_Model_Data_For_Current_Record.reduce((prev, current) =>
            (current?.score ?? -Infinity) > (prev?.score ?? -Infinity)
              ? current
              : prev
          )
        : null;

    setBestScoreModel(bestModel);
    setSelectedModel(bestModel?.model_name || "");
  };

  const setCurrentBestModel = (model) => {
    setSelectedModel(model);
    const RespectiveMetaData = allModelsMetaData.find((item) => {
      return item.model_name === model;
    });
    setBestScoreModel(RespectiveMetaData);
  };

  const setShowModel = () => {
    setExpanded(!expanded);
    setRowSpan(rowSpan === 2 ? 4 : 2);
  };

  const getModelColor = (model) => {
    if (!model) return "";
    const Model_Meta = AllModelsMetaData?.find((item) => item?.model === model);
    return Model_Meta?.color || "";
  };

  const handleSelection = (modelName) => {
    setOverrideModel(modelName);

    const is_Not_A_Model = Object.keys(allModelData).every(
      (key) => key !== modelName
    );

    if (is_Not_A_Model) {
      setSelectedColumns([]);
      const includes_Current_Item = sourceData?.recommendedModel.filter(
        (item) => item.rowData.item_Id !== RowData.item_Id
      );
      updateSourceInputs("recommendedModel", includes_Current_Item);
    }
  };

  const handleDropdowns = (newValue) => {
    if (!Array.isArray(newValue)) return;

    // Check if "select_all" option is selected
    const includesSelectAll = newValue.some(
      (item) => item?.name === "select_all"
    );
    let SELECTED_COLUMNS = [];
    if (includesSelectAll) {
      if (newValue.length === DROPDOWN_OPTIONS.length - 1) {
        SELECTED_COLUMNS = newValue.filter(
          (item) => item?.name !== "select_all"
        );
      } else {
        SELECTED_COLUMNS = DROPDOWN_OPTIONS;
      }
    } else {
      SELECTED_COLUMNS = newValue;
    }
    setSelectedColumns(SELECTED_COLUMNS);

    // Defensive check: ensure recommendedModel is an array
    const recommendedModel = Array.isArray(sourceData?.recommendedModel)
      ? sourceData.recommendedModel
      : [];

    // Look for existing recommendation for this row
    const existingRecommendation = recommendedModel.find(
      (item) => item?.rowData?.item_Id === RowData.item_Id
    );

    // Build new recommendation object
    const newRecommendation = {
      selectedAttributes: SELECTED_COLUMNS,
      rowData: RowData,
      bestScoreModel: bestScoreModel || null,
    };

    let updatedRecommendations;
    if (existingRecommendation) {
      // Replace the matching row
      updatedRecommendations = recommendedModel.map((item) =>
        item?.rowData?.item_Id === RowData.item_Id ? newRecommendation : item
      );
    } else {
      // Add new row
      updatedRecommendations = [...recommendedModel, newRecommendation];
    }

    // Update state
    updateSourceInputs("recommendedModel", updatedRecommendations);
  };

  useEffect(() => {
    sortBestScoreModelAndAllModelMetaData();
  }, []);

  if (
    !bestScoreModel ||
    !allModelsMetaData ||
    Object.keys(bestScoreModel).length === 0 ||
    allModelsMetaData.length === 0
  )
    return (
      <div style={{ width: "100%", textAlign: "center" }}>Loading Data...</div>
    );

  return (
    <>
      {/* Actual row */}
      <TableRow>
        {/* SKU description cell with rowSpan */}
        <StyledTableCell
          style={{ borderRight: "1px solid #dfe1e6" }}
          rowSpan={bestScoreModel ? rowSpan : 1}
        >
          <p>{RowData?.rowData?.[SKU_DESCRIPTION_NAME] || ""}</p>
        </StyledTableCell>

        {TableColumns.filter((c) => c.name !== SKU_DESCRIPTION_NAME).map(
          ({ name }) => (
            <StyledTableCell key={name}>
              <p
                style={{
                  margin: "10px 0px",
                }}
              >
                {name !== "selection" &&
                  name !== "options" &&
                  name !== "model_name" &&
                  name !== "score" && (
                    <span>{RowData.rowData[name] ?? "null"}</span>
                  )}

                {name === "model_name" && <span>-- NA --</span>}

                {name === "score" && <span>-- NA --</span>}

                {name === "options" && <span>-- NA --</span>}
                {name === "selection" && (
                  <input
                    type="checkbox"
                    checked={overideModel === RowData.rowData.model_name}
                    value={RowData.rowData.model_name}
                    onClick={() => handleSelection(RowData.rowData.model_name)}
                  />
                )}
              </p>
            </StyledTableCell>
          )
        )}
      </TableRow>

      {/* Best Recommendation row */}
      {bestScoreModel && bestScoreModel.model_name && (
        <TableRow style={{ backgroundColor: "#E6E8ED" }}>
          {TableColumns.filter((c) => c.name !== SKU_DESCRIPTION_NAME).map(
            ({ name }) => {
              const Model_Color = getModelColor(
                bestScoreModel?.model_name || ""
              );
              return (
                <StyledTableCell>
                  <div
                    style={{
                      display: "flex",
                      gap: "5px",
                    }}
                  >
                    {name === "model_name" && (
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: Model_Color || "#735DA5",
                            color: "white",
                            fontSize: "11px",
                            height: "20px",
                            width: "100%",
                            padding: "0px 5px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            borderRadius: "3px",
                            border: "1px solid #ccc",
                          }}
                        >
                          {bestScoreModel[name] === null ||
                          bestScoreModel[name] === undefined
                            ? "null"
                            : bestScoreModel[name]}
                        </div>
                        <div>
                          <i
                            className="material-icons-outlined "
                            disabled={RowData.isEditing}
                            style={{
                              cursor: RowData.isEditing
                                ? "not-allowed"
                                : "pointer",
                              border: "1px solid #ccc",
                              borderRadius: "3px",
                              backgroundColor: "#f1f2f7",
                              color: "black",
                            }}
                            onClick={() => setShowModel()}
                          >
                            {expanded ? "arrow_drop_up" : "arrow_drop_down"}
                          </i>
                        </div>
                      </div>
                    )}

                    {name !== "model_name" &&
                      name !== "selection" &&
                      name !== "options" && (
                        <span>
                          {bestScoreModel[name] === null ||
                          bestScoreModel[name] === undefined
                            ? "null"
                            : bestScoreModel[name]}
                        </span>
                      )}

                    {name === "selection" && (
                      <input
                        type="checkbox"
                        checked={overideModel === bestScoreModel.model_name}
                        value={bestScoreModel.model_name}
                        onClick={() =>
                          handleSelection(bestScoreModel.model_name)
                        }
                      />
                    )}

                    {name === "options" && (
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        disabled={overideModel !== bestScoreModel.model_name}
                        options={DROPDOWN_OPTIONS || []}
                        value={selectedColumns || []}
                        onChange={(e, newVal) => handleDropdowns(newVal)}
                        getOptionLabel={(option) => option.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          option.name === value.name
                        }
                        renderOption={(props, option, { selected }) => (
                          <li
                            {...props}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <Checkbox
                              checked={selected}
                              sx={{
                                padding: "0px",
                                transform: "scale(0.9)",
                                "& .MuiSvgIcon-root": {
                                  fontSize: 18, // default ~24px → reduce it
                                },
                              }}
                            />
                            <span style={{ fontSize: "12.5px" }}>
                              {option.label}
                            </span>
                          </li>
                        )}
                        renderTags={(value) => {
                          if (value.length === 0) return null;

                          const first = value[0];
                          const rest = value.length - 1;

                          return (
                            <Chip
                              label={
                                rest > 0
                                  ? `${first.label} +${rest} more`
                                  : first.label
                              }
                              size="small"
                              sx={{
                                height: "22px",
                                fontSize: "12px",
                                maxWidth: "140px",
                              }}
                            />
                          );
                        }}
                        popupIcon={
                          <span
                            className="material-icons"
                            style={{ fontSize: "20px", color: "#666" }}
                          >
                            arrow_drop_down
                          </span>
                        }
                        sx={{
                          width: 190,
                          "& .MuiOutlinedInput-root": {
                            height: 36,
                            padding: "0 8px",
                            borderRadius: "6px",
                            fontSize: "12.5px",
                            backgroundColor: "#fff",
                            display: "flex",
                            alignItems: "center", // ✅ center vertically
                          },
                          "& .MuiOutlinedInput-input": {
                            padding: "0px !important",
                            fontSize: "12.5px",
                          },
                          "& .MuiAutocomplete-endAdornment": {
                            top: "50%",
                            transform: "translateY(-50%)",
                          },
                        }}
                        slotProps={{
                          popper: {
                            sx: {
                              "& .MuiAutocomplete-listbox": {
                                fontSize: "13px",
                                padding: "2px 0",
                              },
                              "& .MuiAutocomplete-option": {
                                padding: "4px 8px",
                              },
                            },
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={
                              selectedColumns && selectedColumns.length > 0
                                ? ""
                                : "Select Attributes"
                            }
                            InputLabelProps={{ shrink: false }}
                          />
                        )}
                      />
                    )}
                  </div>
                </StyledTableCell>
              );
            }
          )}
        </TableRow>
      )}

      {expanded &&
        allModelsMetaData &&
        allModelsMetaData.map((modelRecord) => {
          if (!modelRecord) {
            return (
              <div
                style={{
                  fontSize: "16px",
                  color: "black",
                  backgroundColor: "#f1f2f7",
                }}
              >
                No Recommendation found
              </div>
            );
          }

          const Model_Color = getModelColor(modelRecord?.model_name || "");
          return (
            <TableRow style={{ backgroundColor: "#f1f2f7" }}>
              {TableColumns.filter((c) => c.name !== SKU_DESCRIPTION_NAME).map(
                ({ label, name }) => {
                  return (
                    <StyledTableCell>
                      <div
                        style={{
                          display: "flex",
                          gap: "5px",
                        }}
                      >
                        {name === "model_name" && (
                          <span
                            style={{
                              backgroundColor: Model_Color || "#735DA5",
                              color: "white",
                              fontSize: "11px",
                              padding: "2px 5px",
                              borderRadius: "3px",
                              border: "1px solid #ccc",
                            }}
                          >
                            {modelRecord[name] === null ||
                            modelRecord[name] === undefined
                              ? "null"
                              : modelRecord[name]}
                          </span>
                        )}

                        {name !== "model_name" &&
                          name !== "selection" &&
                          name !== "options" && (
                            <span>
                              {modelRecord[name] === null ||
                              modelRecord[name] === undefined
                                ? "null"
                                : modelRecord[name]}
                            </span>
                          )}

                        {name === "options" && (
                          <input
                            type="radio"
                            style={{ marginLeft: "15px", cursor: "pointer" }}
                            checked={selectedModel === modelRecord.model_name}
                            value={modelRecord.model_name}
                            onClick={() =>
                              setCurrentBestModel(modelRecord.model_name)
                            }
                          />
                        )}
                      </div>
                    </StyledTableCell>
                  );
                }
              )}
            </TableRow>
          );
        })}
    </>
  );
};

export const RecommandationTableTwo = (props) => {
  const {
    allModelData,
    TableColumns,
    AllModelsMetaData,
    sourceData,
    updateSourceInputs,
  } = props;

  return (
    <>
      <TableContainer className={Tablestyles.budget_tb2}>
        <Table stickyHeader>
          <TableHead className="recommendation-table-heading">
            <TableRow>
              {[
                ...TableColumns,
                // { label: "Actions", name: "actions", value: "actions" },
              ].map(({ label, name }) => (
                <StyledTableCell>
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      marginLeft: "3px",
                    }}
                  >
                    <span>{label}</span>
                  </div>
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!!sourceData.actualData &&
              sourceData.actualData.map((rowData, index) => {
                return (
                  <Row
                    key={index}
                    TableColumns={TableColumns}
                    RowData={rowData}
                    allModelData={allModelData}
                    AllModelsMetaData={AllModelsMetaData}
                    sourceData={sourceData}
                    updateSourceInputs={updateSourceInputs}
                  />
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
